<!--
  - Copyright (c) ProviderTrust LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - ProviderTrust LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with ProviderTrust.
  -->

<template>
  <div class="landing">
    <section class="main-content">
      <header class="navbar">
        <div>
          <a href="/" title="Interactive Search Main">
            <img
              src="../assets/ECLogoWithProviderTrust.png"
              alt="ExclusionCheck"
              class="app-nav-logo"
            />
          </a>
        </div>
        <nav>
          <ul>
            <li>
              <v-btn depressed class="hidden-xs-only login-btn" to="/login"
                >Log in
              </v-btn>
            </li>
            <li>
              <v-btn
                depressed
                class="last-btn"
                :class="{ 'ma-0': $vuetify.breakpoint.xsOnly }"
                color="primary"
                to="/registration/company"
                >Sign Up
              </v-btn>
            </li>
          </ul>
        </nav>
      </header>

      <v-layout class="main-content-container" row wrap>
        <v-flex xs12 sm6 class="introduction">
          <h1>Exclusion searching made simple: every list in one place</h1>
          <hr class="content-spliter" />
          <p>
            ExclusionCheck is the fastest and most comprehensive OIG and
            Medicaid exclusion searchmachine, engineered to help protect your
            organization by finding employee / vendor data straight from the
            source!
          </p>

          <ul class="check-circle-list hidden-xs-only">
            <li>OIG-LEIE</li>
            <li>SAM</li>
            <li>All State Medicaid Exclusion Lists</li>
          </ul>
        </v-flex>

        <v-flex xs12 sm6 class="search-form-wrapper">
          <InteractiveSearchForm />
        </v-flex>
      </v-layout>
    </section>

    <section
      class="text-xs-center"
      :class="{ 'px-3': $vuetify.breakpoint.xsOnly }"
    >
      <p class="mid-banner-cta">
        Exclusion searches have never been easier, sign up for unlimited
        searches with a monthly plan that's right for you!
        <br class="hidden-sm-and-up" />
        <v-btn depressed color="primary" class="cta" to="/registration/company">
          Sign up
        </v-btn>
      </p>
    </section>

    <section
      class="text-xs-center main-details"
      :class="{
        'xs-padding': $vuetify.breakpoint.xsOnly,
        'sm-padding': $vuetify.breakpoint.smAndUp
      }"
    >
      <h2>
        Here’s how searching with ExclusionCheck keeps your organization
        protected
      </h2>

      <v-layout row wrap class="details-container">
        <v-flex xs12 sm4>
          <div class="details">
            <v-icon>fa fa-list-alt</v-icon>
            <h3>All your bases, covered</h3>
            <p>
              Search every federal and state exclusion list for individuals and
              companies
            </p>
          </div>
        </v-flex>

        <v-flex xs12 sm4>
          <div class="details">
            <v-icon>fa fa-th-large</v-icon>
            <h3>Save time, search in bulk</h3>
            <p>
              Upload batch files to search more than one name or NPI at a time
            </p>
          </div>
        </v-flex>

        <v-flex sm4>
          <div class="details">
            <v-icon>fa fa-lightbulb-o</v-icon>
            <h3>Work smarter</h3>
            <p>Track records you’ve verified as matches or non-matches.</p>
          </div>
        </v-flex>

        <v-flex sm4>
          <div class="details">
            <v-icon>fa fa-history</v-icon>
            <h3>Make audits and reports painless</h3>
            <p>
              Generate reports and show your work with ExclusionCheck search
              history
            </p>
          </div>
        </v-flex>

        <v-flex sm4>
          <div class="details">
            <v-icon>fa fa-users</v-icon>
            <h3>Share the work</h3>
            <p>
              Invite as many co-workers as you need to give your organization
              the flexibility it needs
            </p>
          </div>
        </v-flex>

        <v-flex sm4>
          <div class="details">
            <v-icon>fa fa-shopping-cart</v-icon>
            <h3>Pay for what you use</h3>
            <p>
              Pay per search and get billed monthly based on the number of
              searches performed
            </p>
          </div>
        </v-flex>
      </v-layout>

      <h3>Plans start at $30/month</h3>
      <v-btn class="cta" to="/registration/company">Sign up now </v-btn>
    </section>

    <v-layout row wrap justify-center class="bottom-banner-cta">
      <v-flex sm5>
        <h3>Need to join your company’s account?</h3>
        <p>
          Contact your ExclusionCheck administrator and have them invite you.
        </p>
      </v-flex>

      <v-flex sm5>
        <h3>Want to try it out before you commit?</h3>
        <p>Start a trial to get 10 searches per month for free.</p>
        <div class="text-xs-center text-sm-left">
          <v-btn depressed color="primary" class="cta" to="/registration/trial"
            >Start my trial
          </v-btn>
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import InteractiveSearchForm from "@/components/InteractiveSearchForm"
import analytics from "@/service/analytics"

export default {
  name: "Landing",
  components: { InteractiveSearchForm },

  created: function() {
    analytics.logAnalytic("Visits Landing Page")
  }
}
</script>

<style lang="scss">
$bigger-font: 1.13rem; // 18px
$main-bg-color: $color-ec-lightest-gray;
$btn-border-radius: 0.25rem;
$btn-box-shadow: 0 8px 8px 0 rgba(0, 0, 0, 0.24), 0 0 8px 0 rgba(0, 0, 0, 0.12),
  0 2px 2px 0 rgba(0, 0, 0, 0.24), 0 0 2px 0 rgba(0, 0, 0, 0.12);
.landing {
  font-weight: $normal;

  .search-form-wrapper {
    @media only screen and (min-width: 600px) {
      padding-left: 1.125rem;
    }

    .interactive-search-form {
      padding: 1rem 0.75rem 1.5rem;
      @media only screen and (min-width: 600px) {
        border-radius: 0.5rem;
        padding: 1.685rem 1.5rem 1rem;
      }
      background-color: $main-bg-color;

      h2.form-title {
        font-weight: $heavy;
        font-size: 1.1rem;
        line-height: 1.4rem;
        margin-bottom: 1.625rem;
        @media only screen and (min-width: 600px) {
          margin-bottom: 2rem;
        }
      }
      form {
        @media only screen and (min-width: 600px) {
          padding-left: 0.44rem;
        }
      }

      .form-group {
        margin-bottom: 1rem;
        label {
          line-height: 1.2;
          margin-right: 0.75rem;
          vertical-align: middle;
        }
      }
      .v-btn-toggle {
        box-shadow: none;
        vertical-align: middle;
        .v-btn {
          opacity: 0.7;
          color: $color-ec-black1;
          background-color: #d6d6d6;
          &.v-btn--active {
            opacity: 1;
            background-color: $color-ec-orange1;
            color: $color-white;
          }
          &:first-child {
            border-radius: $btn-border-radius 0 0 $btn-border-radius;
          }
          &:last-child {
            border-radius: 0 $btn-border-radius $btn-border-radius 0;
          }
        }
      }
      .search-btn {
        height: 2.25rem;
        width: 250px;
        font-size: $bigger-font;
        font-weight: $heavy;
        box-shadow: $btn-box-shadow !important;
      }
      .form-error {
        border: none !important;
        font-size: 1rem;
        padding: 0 0 0.25rem;
        > .v-icon {
          margin-right: 0.25rem;
        }
      }
    }
  }
  .main-details {
    color: $color-ec-black1;
    &.sm-padding {
      padding: 3.4rem 9rem;
    }
    &.xs-padding {
      padding: 2.85rem 2.375rem;
    }
    background-color: $main-bg-color;
    > h2 {
      color: $color-ec-blue;
      font-size: 1.5rem;
      font-weight: $heavy;
      margin: 0;
    }
    .details-container {
      padding: 1.3rem 0;
      .details {
        @media only screen and (min-width: 600px) {
          padding: 1.3rem 1rem;
        }
        padding: 1.13rem 0;

        p {
          margin-bottom: 0;
        }
        h3 {
          margin: 0.75rem 0 0;
          font-weight: $heavy;
          font-size: 1rem;
          line-height: 1.5rem;
        }
        .v-icon {
          font-size: 1.5rem;
          color: $color-ec-blue;
        }
      }
    }
    > h3 {
      margin: 0.375rem;
    }
    > .v-btn.cta {
      font-size: $bigger-font;
      height: 3rem;
      width: 20rem;
      background-color: $color-ec-orange1;
      box-shadow: $btn-box-shadow !important;
    }
  }

  .v-btn {
    // !important is required for now even when 'depressed' is used. It seems to be a bug when 'to' is used in 'v-btn'
    box-shadow: none !important;
    border-radius: $btn-border-radius;
    color: $color-white;
    text-transform: inherit;
    text-decoration: none;
    font-size: 1rem;
    font-weight: $normal;
    height: 2rem;
    .v-icon--left {
      font-size: 1rem;
      margin-right: 0.2rem;
    }
    &.cta {
      font-weight: $heavy;
      background-color: $color-ec-blue;
    }
  }
}

hr.content-spliter {
  height: 2px;
  color: #ff9900;
  background: #ff9900;
  border: 0;
}

.main-content {
  color: $color-ec-black1;
  background-color: $color-white;

  header.navbar {
    padding: 0.75rem 0.75rem 0;
    @media only screen and (min-width: 600px) {
      padding: 1.625rem 1.625rem 0;
    }

    display: flex;
    justify-content: space-between;
    .app-nav-logo {
      height: 30px;
      @media only screen and (min-width: 600px) {
        height: 40px;
      }
    }
    ul > li {
      display: inline;
    }
    .v-btn {
      &.login-btn {
        margin-right: 0.25rem;
        .v-btn__content {
          background-color: $main-bg-color;
          color: $color-ec-black1;
        }
      }
      &.last-btn {
        margin-right: 0;
      }
    }
  }
  .v-btn {
    font-weight: 600;
  }
  .main-content-container {
    max-width: 960px;
    margin: 0 auto;
    padding-top: 5.125rem;

    @media only screen and (min-width: 600px) {
      padding: 6.375rem 1rem 4.25rem;
    }
    @media only screen and (min-width: 960px) {
      padding-left: 0;
      padding-right: 0;
    }

    .introduction {
      padding: 0 0.75rem;
      @media only screen and (min-width: 600px) {
        padding-right: 1.125rem;
      }

      h1 {
        font-size: 1.5rem;
        font-weight: $heavy;
        line-height: 2.4rem;
        margin-bottom: 1rem;
        @media only screen and (min-width: 600px) {
          font-size: 2rem;
        }
      }
      p {
        margin: 1rem 0 2.625rem 0;
      }
    }
  }
}

.mid-banner-cta {
  margin: 0.85rem 0;
  font-weight: $heavy;
  color: $color-ec-blue;
  background-color: #dcdcdc;
  .v-btn.cta {
    margin-top: 1.19rem;
    @media only screen and (min-width: 600px) {
      margin: 0 0 0 1.25rem;
    }
  }
}

.bottom-banner-cta {
  padding: 2rem;
  h3 {
    font-weight: $heavy;
    font-size: 1rem;
    line-height: 1.5rem;
  }
}

ul.check-circle-list {
  > li {
    margin-top: 0.25rem;
    &:before {
      @include ec-fa-icon(check-circle);
      margin-right: 0.25rem;
    }
  }
}

.clearfix:after {
  content: " ";
  visibility: hidden;
  display: block;
  height: 0;
  clear: both;
}
</style>



// WEBPACK FOOTER //
// src/components/Landing.vue